import { useMemo } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useI18nContext } from '@/i18n/i18n-react';
import { chartColor } from '@/lib/chart-colors';
import {
  isOrdersCompletionClickable,
  type OrdersCompletionSegment,
} from '@/lib/orders-drilldown';

/** chart-1 — completed (paid) orders */
export const COMPLETED_ORDERS_COLOR = chartColor(1);
/** chart-3 — orders created but not paid */
export const NOT_COMPLETED_ORDERS_COLOR = chartColor(3);

const EMPTY_COLOR = 'hsl(var(--muted))';

type SegmentKey = OrdersCompletionSegment | 'empty';

type Segment = {
  key: SegmentKey;
  value: number;
  label: string;
  color: string;
};

interface OrdersCompletionDonutProps {
  completedCount: number;
  notCompletedCount: number;
  /** When provided, slices are clickable. */
  onSegmentClick?: (segment: OrdersCompletionSegment) => void;
}

function DonutTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: Segment }>;
}) {
  if (!active || !payload?.length) return null;
  const segment = payload[0]?.payload;
  if (!segment || segment.key === 'empty') return null;
  return (
    <div className="rounded-lg border border-input bg-background px-2 py-1.5 text-xs shadow-md">
      <p className="flex items-center gap-1.5 font-medium text-foreground">
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ backgroundColor: segment.color }}
        />
        {segment.label}
      </p>
      <p className="text-muted-foreground">{segment.value}</p>
    </div>
  );
}

export function OrdersCompletionDonut({
  completedCount,
  notCompletedCount,
  onSegmentClick,
}: OrdersCompletionDonutProps) {
  const { LL } = useI18nContext();
  const total = completedCount + notCompletedCount;
  const hasAnyData = total > 0;

  const data = useMemo((): Segment[] => {
    if (!hasAnyData) {
      return [{ key: 'empty', value: 1, label: '', color: EMPTY_COLOR }];
    }
    const segments: Segment[] = [
      {
        key: 'completed',
        value: completedCount,
        label: LL.metricsPage.ordersCompletion.completed.label(),
        color: COMPLETED_ORDERS_COLOR,
      },
      {
        key: 'notCompleted',
        value: notCompletedCount,
        label: LL.metricsPage.ordersCompletion.notCompleted.label(),
        color: NOT_COMPLETED_ORDERS_COLOR,
      },
    ];
    return segments.filter((segment) => segment.value > 0);
  }, [
    completedCount,
    notCompletedCount,
    hasAnyData,
    LL.metricsPage.ordersCompletion,
  ]);

  // Resolved by the entry's key, not the index: empty slices are filtered out.
  const handleSliceClick = (_: unknown, index: number) => {
    const entry = data[index];
    if (!onSegmentClick || !entry || entry.key === 'empty') return;
    onSegmentClick(entry.key);
  };
  const isClickable = isOrdersCompletionClickable({
    completedCount,
    notCompletedCount,
    enabled: onSegmentClick !== undefined,
  });

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
        <Pie
          data={data}
          dataKey="value"
          nameKey="label"
          cx="50%"
          cy="50%"
          innerRadius="85%"
          outerRadius="100%"
          startAngle={90}
          endAngle={-270}
          paddingAngle={data.length > 1 ? 4 : 0}
          strokeWidth={0}
          isAnimationActive={false}
          tabIndex={-1}
          style={{ outline: 'none' }}
          onClick={isClickable ? handleSliceClick : undefined}
        >
          {data.map((entry) => (
            <Cell
              key={entry.key}
              fill={entry.color}
              tabIndex={-1}
              style={{
                outline: 'none',
                cursor: isClickable ? 'pointer' : undefined,
              }}
            />
          ))}
        </Pie>
        {hasAnyData ? (
          <Tooltip
            content={<DonutTooltip />}
            cursor={false}
            wrapperStyle={{ outline: 'none', zIndex: 50 }}
          />
        ) : null}
      </PieChart>
    </ResponsiveContainer>
  );
}
